import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../widgets";
import PatientPersonalInfo from "../../patientFullView/patientPersonalInfo";
import PatientHistoryItem from "./PatientHistoryItem";
import { getPatientHistory } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import strings from "../../../utils/strings";
import "./PatientHistoryDetails.css";

const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

// Formats a "yyyy-mm-dd" filter input value as "dd/mm/yyyy" without going through Date/timezone parsing
const formatFilterDate = (dateStr) => {
    if (!dateStr) return "-";
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}/${mm}/${yyyy}`;
};

const toDateStr = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const formatTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
};

const formatVND = (value) => {
    const n = Math.round(Number(value) || 0);
    return `₫${n.toLocaleString("vi-VN")}`;
};

const PatientHistoryDetails = () => {
    const { t } = useTranslation();
    const { hnNumber } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { patientHistoryData } = useSelector((state) => state.manageRequest);
    const [expandedItems, setExpandedItems] = useState({});
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const breadcrumbs = ["nursingCare", "patientHistory", "patientDetails"];
    const paths = [
        { path: strings.navigation.nursingCareManageService },
        {
            path: strings.navigation.patientHistory,
            onClick: () => navigate(strings.navigation.patientHistory),
        },
        { path: strings.navigation.patientHistoryDetails },
    ];

    useEffect(() => {
        if (!patientHistoryData || patientHistoryData.patient?.hnNumber !== hnNumber) {
            dispatch(getPatientHistory({ hnNumber }));
        }
    }, [hnNumber]);

    useEffect(() => {
        if (patientHistoryData?.sessions?.data?.length > 0) {
            setExpandedItems({ [patientHistoryData.sessions.data[0]._id]: true });
        }
    }, [patientHistoryData]);

    const handleToggle = (itemId) => {
        setExpandedItems((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    };

    if (!patientHistoryData) {
        return (
            <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
                <Widgets.LoadingContainer />
            </div>
        );
    }

    const { patient, addresses, sessions } = patientHistoryData;

    const patientDetails = {
        firstName: patient.firstName,
        lastName: patient.lastName,
        hnNumber: patient.hnNumber,
        gender: patient.gender,
        dateOfBirth: patient.dateOfBirth,
        countryCode: `+${patient.countryCode}`,
        phoneNumber: patient.phoneNumber,
        email: patient.email,
        insuranceCategory: patient.insuranceCategory,
        profilePhoto: patient.profilePhoto,
    };

    const addr = addresses?.[0];
    const addressString = addr
        ? [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean).join(", ")
        : "-";

    const allSessions = sessions?.data || [];

    // Apply date filter
    const filteredSessions = allSessions.filter((session) => {
        const sessionDate = toDateStr(session.startTime);
        if (filterFrom && sessionDate < filterFrom) return false;
        if (filterTo && sessionDate > filterTo) return false;
        return true;
    });

    // Build history items with all details
    const historyItems = filteredSessions.map((session) => {
        const nurseArr = Array.isArray(session.nurse) ? session.nurse : session.nurse ? [session.nurse] : [];
        const nurseNames = nurseArr
            .map((n) => `${n.firstName?.en || n.firstName || ""} ${n.lastName?.en || n.lastName || ""}`.trim())
            .join(", ");
        const nurseIds = nurseArr.map((n) => n.employeeID || "").filter(Boolean).join(", ");

        const startTime = formatTime(session.startTime);
        const endTime = formatTime(session.endTime);
        const scheduledTime = session.startTime ? `${startTime}${session.endTime ? ` → ${endTime}` : ""}` : "-";

        const checkInTime = session.checkInTime || session.checkinTime || session.checkIn
            ? formatTime(session.checkInTime || session.checkinTime || session.checkIn)
            : null;
        const checkOutTime = session.checkOutTime || session.checkoutTime || session.checkOut
            ? formatTime(session.checkOutTime || session.checkoutTime || session.checkOut)
            : null;

        const firstServiceItem = Array.isArray(session.serviceItem) ? session.serviceItem[0] : session.serviceItem;
        const firstSubService = Array.isArray(session.subService) ? session.subService[0] : session.subService;
        const firstService = Array.isArray(session.service) ? session.service[0] : session.service;
        const isValidLabel = (v) => v && typeof v === "string" && v.trim().toLowerCase() !== "non" && v.trim() !== "-";
        const pickLabel = (...candidates) => candidates.find(isValidLabel) || "-";
        const serviceLabel = pickLabel(
            firstServiceItem?.title?.en, firstServiceItem?.title?.vi,
            typeof firstServiceItem?.title === "string" ? firstServiceItem.title : null,
            typeof firstServiceItem === "string" ? firstServiceItem : null,
            session.serviceItemName,
            firstSubService?.title?.en, firstSubService?.title?.vi,
            typeof firstSubService?.title === "string" ? firstSubService.title : null,
            typeof firstSubService === "string" ? firstSubService : null,
            firstService?.title?.en, firstService?.title?.vi,
            typeof firstService?.title === "string" ? firstService.title : null,
            typeof firstService === "string" ? firstService : null,
        );
        const serviceType = serviceLabel;

        const sessionAddr = session.address || session.serviceAddress;
        const patientAddress = sessionAddr
            ? (typeof sessionAddr === "string"
                ? sessionAddr
                : [sessionAddr.street, sessionAddr.ward, sessionAddr.city, sessionAddr.province, sessionAddr.postalCode].filter(Boolean).join(", "))
            : addressString;

        const fee = session.price ? formatVND(session.price) : null;

        return {
            id: session._id,
            date: formatDate(session.startTime),
            serviceType,
            scheduledTime,
            checkInTime,
            checkOutTime,
            fee,
            staffName: nurseNames || null,
            staffId: nurseIds || null,
            patientAddress,
            woundCare: {
                images: session.checkoutAttachments || session.uploadedImages || session.images || [],
                observations: session.nurseObservations || session.observations || "-",
                notes: session.checkoutNotes || session.notes || session.nurseNotes || session.checkoutNotesSummary || "-",
            },
            notes: session.checkoutNotes || session.notes || session.nurseNotes || session.checkoutNotesSummary || "-",
            userFeedback: session.userFeedback || null,
            nurseFeedback: session.nurseFeedback || null,
        };
    });

    // Date range display — reflect the currently applied filter; fall back to the full session range when unset
    const sorted = [...allSessions].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const fromDate = filterFrom ? formatFilterDate(filterFrom) : formatDate(sorted[0]?.startTime);
    const toDate = filterTo ? formatFilterDate(filterTo) : formatDate(sorted[sorted.length - 1]?.startTime);

    return (
        <div className="phd-container">
            <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
            <div className="department-heading">
                <div>{t("patientHistoryViewer")}</div>
            </div>

            <PatientPersonalInfo data={patientDetails} />

            <div className="phd-address-section">
                <p className="phd-section-title">{t("patientAddress")}</p>
                <div className="phd-address-content">
                    <p className="phd-address-text">{addressString}</p>
                </div>
            </div>

            <div className="phd-history-section">
                <div className="phd-history-header">
                    <p className="phd-section-title">{t("patientHistory")}</p>
                    <div className="phd-date-filter-row">
                        <span className="phd-date-label">{t("from") || "From"}:</span>
                        <input
                            type="date"
                            value={filterFrom}
                            onChange={(e) => setFilterFrom(e.target.value)}
                            className="phd-date-filter-input"
                        />
                        <span className="phd-date-label">{t("to") || "To"}:</span>
                        <input
                            type="date"
                            value={filterTo}
                            onChange={(e) => setFilterTo(e.target.value)}
                            className="phd-date-filter-input"
                        />
                        {(filterFrom || filterTo) && (
                            <button
                                className="phd-clear-filter-btn"
                                onClick={() => { setFilterFrom(""); setFilterTo(""); }}
                            >
                                {t("clear") || "Clear"}
                            </button>
                        )}
                    </div>
                </div>
                <div className="phd-date-range">
                    <span className="phd-date-label">{t("fromDate")}:</span>
                    <span className="phd-date-value">{fromDate}</span>
                    <span className="phd-date-separator">-</span>
                    <span className="phd-date-label">{t("toDate")}:</span>
                    <span className="phd-date-value">{toDate}</span>
                </div>
                <div className="phd-history-list">
                    {historyItems.length === 0 ? (
                        <p style={{ fontSize: "1.3rem", color: "#888", padding: "2rem 0" }}>
                            {t("noDataFound") || "No sessions found for the selected date range."}
                        </p>
                    ) : (
                        historyItems.map((item) => (
                            <PatientHistoryItem
                                key={item.id}
                                historyItem={item}
                                isExpanded={expandedItems[item.id]}
                                onToggle={() => handleToggle(item.id)}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default PatientHistoryDetails;
